// src/admin/pages/AssignTenantModal.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdminModal from '../components/AdminModal.jsx';
import { assignUserToTenant } from '../adminApi.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
  color: COLORS.ink, fontSize: '14px', boxSizing: 'border-box',
  outline: 'none', fontFamily: FONT_SANS,
};

const labelStyle = {
  display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px', fontWeight: 500,
};

const ROLES = ['viewer', 'editor', 'admin'];

// Our words for a refused assignment, keyed on err.code — never the server's
// English. Codes the active backend returns on POST /admin/tenants/{tid}/users.
// ⚠️ "No such account" is 404 RESOURCE_NOT_FOUND on THIS route (the tenant-admin
// routes say USER_NOT_FOUND). The route has no seat gate.
function assignErrorCopy(t, err) {
  switch (err?.code) {
    case 'INVALID_ROLE': return t('tenantUsers.invalidRole');
    case 'CANNOT_ATTACH_SUPERADMIN': return t('tenantUsers.cannotInviteSuperadmin');
    case 'USER_TENANT_ASSIGNMENT_EXISTS': return t('tenantUsers.alreadyMember');
    case 'RESOURCE_NOT_FOUND': return t('tenantUsers.userNotFound');
    default: return t('tenantUsers.genericError', { code: err?.code || err?.status || '—' });
  }
}

export default function AssignTenantModal({ open, onClose, userEmail, availableTenants, onAssigned }) {
  const { t } = useTranslation('admin');
  const [tenantId, setTenantId] = useState('');
  const [role, setRole] = useState('editor');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!tenantId) { setError(t('assignTenant.selectError')); return; }
    setError('');
    setLoading(true);
    try {
      await assignUserToTenant(tenantId, userEmail, role);
      setTenantId('');
      setRole('editor');
      onAssigned();
      onClose();
    } catch (err) {
      // No seat branch here: the superadmin route POST /admin/tenants/{tid}/users
      // has no seat gate on the active backend, so it never answers with a seat
      // code. The tenant-admin twins in tenantAdminApi.js are the gated paths.
      setError(assignErrorCopy(t, err));
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setTenantId('');
    setRole('editor');
    setError('');
    onClose();
  }

  return (
    <AdminModal open={open} onClose={handleClose} title={t('assignTenant.title')} width={420}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>{t('shared.tenant')}</label>
          <select value={tenantId} onChange={e => setTenantId(e.target.value)} style={inputStyle}>
            <option value="">{t('shared.selectPlaceholder')}</option>
            {availableTenants.map(tn => (
              <option key={tn.id} value={tn.id}>{tn.display_name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>{t('shared.role')}</label>
          <select value={role} onChange={e => setRole(e.target.value)} style={inputStyle}>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {error && (
          <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}`, color: SEMANTIC.errore.fg, fontSize: '13px' }}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%', padding: '10px', borderRadius: RADII.controlInner, border: 'none',
            background: loading ? COLORS.borderStrong : COLORS.accent, color: COLORS.surface,
            fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? t('assignTenant.assigning') : t('assignTenant.assign')}
        </button>
      </form>
    </AdminModal>
  );
}
